import React, { useState, useEffect } from 'react';
import { Phone, Mail, MessageCircle, Linkedin } from 'lucide-react';

interface AgentHeaderProps {
  status: 'active' | 'idle' | 'processing';
  lastActivity?: Date;
  title?: string;
  subtitle?: string;
  avatarSrc?: string;
  avatarAlt?: string;
  activeLabel?: string;
  processingMessages?: string[];
  icon?: 'phone' | 'mail' | 'whatsapp' | 'linkedin';
}

const defaultProcessingMessages = [
  'Analyzing call patterns',
  'Preparing summaries',
  'Optimizing call scripts',
  'Reviewing lead data',
  'Planning next actions',
  'Updating insights',
  'Processing feedback',
  'Calibrating strategies'
];

const AgentHeader: React.FC<AgentHeaderProps> = ({
  status,
  lastActivity,
  title = 'Call Agent',
  subtitle = 'I call leads, qualify them, and schedule meetings.',
  avatarSrc,
  avatarAlt = 'Call Agent',
  activeLabel = 'Active Now',
  processingMessages = defaultProcessingMessages,
  icon = 'phone'
}) => {
  const [processingMessageIndex, setProcessingMessageIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const getIcon = () => {
    switch (icon) {
      case 'phone':
        return <Phone className="w-12 h-12 text-white" />;
      case 'mail':
        return <Mail className="w-12 h-12 text-white" />;
      case 'whatsapp':
        return <MessageCircle className="w-12 h-12 text-white" />;
      case 'linkedin':
        return <Linkedin className="w-12 h-12 text-white" />;
      default:
        return <Phone className="w-12 h-12 text-white" />;
    }
  };

  useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
  }, [avatarSrc]);

  useEffect(() => {
    if (status === 'processing') {
      const interval = setInterval(() => {
        setProcessingMessageIndex(prev => (prev + 1) % processingMessages.length);
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [status, processingMessages.length]);

  const getStatusInfo = () => {
    switch (status) {
      case 'active':
        return {
          label: activeLabel,
          color: 'bg-[#05E0D1]',
          text: 'text-[#05E0D1]',
          bgColor: 'bg-[#05E0D1]/10',
          borderColor: 'border-[#05E0D1]/30'
        };
      case 'idle':
        return {
          label: 'Idle',
          color: 'bg-[#52B5AE]',
          text: 'text-[#52B5AE]',
          bgColor: 'bg-[#52B5AE]/10',
          borderColor: 'border-[#52B5AE]/30'
        };
      case 'processing':
        return {
          label: processingMessages[processingMessageIndex],
          color: 'bg-[#1875FF]',
          text: 'text-[#1875FF]',
          bgColor: 'bg-[#1875FF]/10',
          borderColor: 'border-[#1875FF]/30'
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="bg-gradient-to-br from-white via-[#05E0D1]/5 to-[#1875FF]/10 rounded-2xl p-8 shadow-2xl relative overflow-hidden border-2 border-[#05E0D1]/20">
      <div className="absolute inset-0 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5 animate-pulse"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#05E0D1]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#1875FF]/10 rounded-full blur-3xl"></div>

      <div className="relative flex items-center justify-between">
        <div className="flex items-center space-x-6">
          <div className="relative group">
            <div className="absolute inset-0 bg-[#05E0D1]/30 opacity-60 rounded-full blur-2xl animate-pulse"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '500ms' }}></div>
            <div className="relative w-24 h-24 bg-gradient-to-br from-[#1875FF] via-[#05E0D1] to-[#52B5AE] rounded-full flex items-center justify-center shadow-2xl ring-4 ring-[#05E0D1]/40 group-hover:ring-[#05E0D1]/60 transition-all duration-300 group-hover:scale-105 overflow-hidden">
              {avatarSrc && !imageError ? (
                <img
                  src={avatarSrc}
                  alt={avatarAlt}
                  className="w-full h-full object-cover"
                  onLoad={() => setImageLoaded(true)}
                  onError={() => {
                    console.warn(`Failed to load avatar image: ${avatarSrc}`);
                    setImageError(true);
                  }}
                  loading="eager"
                />
              ) : (
                getIcon()
              )}
            </div>
          </div>

          <div>
            <h1 className="text-4xl font-extrabold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1] font-display tracking-tight">
              {title}
            </h1>
            <p className="text-gray-700 text-base max-w-md leading-relaxed font-medium">
              {subtitle}
            </p>
          </div>
        </div>

        <div className={`flex items-center space-x-3 px-5 py-3 rounded-xl ${statusInfo.bgColor} border-2 ${statusInfo.borderColor} shadow-lg backdrop-blur-sm`}>
          <div className={`w-3 h-3 rounded-full ${statusInfo.color} animate-pulse shadow-lg`}></div>
          <span className={`font-bold text-base ${statusInfo.text}`}>
            {statusInfo.label}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AgentHeader;
